import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { addPreLeadLog } from '../../services/api/implementation/impl';
import {
  getPreLeadLog,
  getPreLeadReason,
  isPreleadSettled
} from '../../utils/preleadLogUtils';

const NOTE_BUTTON_CLASS =
  'inline-flex items-center rounded-lg border border-amber-600/40 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 transition-colors';

export function PreleadNoteButton({ row, onClick }) {
  const settled = isPreleadSettled(row);
  const hasReason = Boolean(getPreLeadReason(row));
  const hasLog = Boolean(getPreLeadLog(row));

  if (settled) {
    if (!hasReason && !hasLog) return <span className="text-gray-400 text-xs">—</span>;
    return (
      <button type="button" onClick={onClick} className={NOTE_BUTTON_CLASS}>
        View note
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick} className={NOTE_BUTTON_CLASS}>
      {hasLog ? 'View note' : 'Add note'}
    </button>
  );
}

const PreleadLogModal = ({ prelead, onClose, onLogAdded }) => {
  const preleadId = prelead?.id ?? prelead?.pre_lead_id;
  const displayName = prelead?.name && prelead.name !== '—' ? prelead.name : `Prelead #${preleadId ?? ''}`;
  const settled = isPreleadSettled(prelead);
  const isQualified = prelead?.lead_id != null;
  const reason = getPreLeadReason(prelead);
  const log = getPreLeadLog(prelead);

  const [logText, setLogText] = useState(() => getPreLeadLog(prelead));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setLogText(getPreLeadLog(prelead));
  }, [prelead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = logText.trim();
    if (!trimmed) {
      toast.error('Please enter a note.');
      return;
    }
    if (!preleadId) {
      toast.error('Invalid prelead.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await addPreLeadLog(preleadId, trimmed);
      const saved = getPreLeadLog(result) || trimmed;
      setLogText(saved);
      toast.success('Telecalling note saved.');
      onLogAdded?.(result);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save note.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal width={42} name={`Note — ${displayName}`} onClose={onClose}>
      {settled ? (
        <div className="space-y-3">
          {isQualified && (
            <span className="inline-flex rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-800 ring-1 ring-green-600/30">
              Qualified
            </span>
          )}
          {prelead?.is_disqualified && (
            <span className="inline-flex rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800 ring-1 ring-red-600/30">
              Unqualified
            </span>
          )}
          {log ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Telecalling note</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">{log}</p>
            </div>
          ) : null}
          {reason ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Reason</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">{reason}</p>
            </div>
          ) : null}
          {!log && !reason ? <p className="text-sm text-gray-500">No note available.</p> : null}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <label htmlFor="prelead-log-input" className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Telecalling note
          </label>
          <textarea
            id="prelead-log-input"
            rows={4}
            value={logText}
            onChange={(e) => setLogText(e.target.value)}
            placeholder="e.g. Called today, follow up Monday"
            className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue"
            disabled={isSubmitting}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !logText.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-customBlue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {isSubmitting ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PreleadLogModal;
